import { DEFAULT_SCOPE, defaultConditionName, matrixScopes } from './config'
import { LIBRARY_TAB_RC_CONFIG } from './rcConfig'
import type {
  CountryCode,
  LibraryTabDraft,
  LibraryTabItem,
  RcConditionPreview,
  RcTabEntry,
} from './types'

function orderedItemsForCountry(
  draft: LibraryTabDraft,
  country: CountryCode,
): LibraryTabItem[] {
  const visible = draft.items.filter((item) => item.visibleIn.includes(country))
  const state = draft.countryOrders[country]

  if (!state || state.mode === 'follow') {
    return visible
  }

  const byId = new Map(visible.map((item) => [item.id, item]))
  const ordered: LibraryTabItem[] = []
  for (const id of state.order) {
    const item = byId.get(id)
    if (item) {
      ordered.push(item)
      byId.delete(id)
    }
  }
  for (const item of byId.values()) {
    ordered.push(item)
  }
  return ordered
}

function toRcEntry(
  item: LibraryTabItem,
  country: CountryCode,
  platform: LibraryTabDraft['platform'],
): { entry?: RcTabEntry; warning?: string } {
  const title = (item.titles[country] ?? '').trim()
  const tag = item.tag.trim()
  const type = item.type?.trim()

  if (!tag) {
    return { warning: `Empty tag (id=${item.id})` }
  }
  if (!title) {
    return { warning: `Missing title for tag "${tag}"` }
  }

  if (platform === 'ios') {
    return {
      entry: type ? { title, key: tag, type } : { title, key: tag },
    }
  }
  return {
    entry: type ? { title, tag, type } : { title, tag },
  }
}

function buildScopePreview(
  draft: LibraryTabDraft,
  code: CountryCode,
  isDefault: boolean,
): RcConditionPreview {
  const condition =
    draft.conditionNames?.[code] ||
    defaultConditionName(draft.product, draft.platform, code)
  const ordered = orderedItemsForCountry(draft, code)
  const entries: RcTabEntry[] = []
  const warnings: string[] = []

  for (const item of ordered) {
    const { entry, warning } = toRcEntry(item, code, draft.platform)
    if (warning) warnings.push(warning)
    if (entry) entries.push(entry)
  }

  return {
    condition,
    country: code,
    platform: draft.platform,
    isDefault,
    entries,
    warnings,
  }
}

export function buildRcPreviews(draft: LibraryTabDraft): RcConditionPreview[] {
  const scopes = matrixScopes(draft.countries ?? [])
  return scopes.map((code) =>
    buildScopePreview(draft, code, code === DEFAULT_SCOPE),
  )
}

export function formatRcJson(entries: RcTabEntry[]): string {
  return JSON.stringify(entries, null, 2)
}

export function formatAllRcJson(previews: RcConditionPreview[]): string {
  const conditions: Record<string, RcTabEntry[]> = {}
  for (const preview of previews) {
    conditions[preview.condition] = preview.entries
  }
  return JSON.stringify(
    {
      parameter: LIBRARY_TAB_RC_CONFIG.parameterKey,
      note: `通用 = Neku_*${LIBRARY_TAB_RC_CONFIG.conditionSuffix || ''} (no country). Parameter defaultValue stays empty. Versioned names append _Version.`,
      default: '',
      conditions,
    },
    null,
    2,
  )
}
